import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Controller, useFormContext, UseFormReturn } from 'react-hook-form';
import { Icon, Typography } from '@sravni/react-design-system';
import { Edit } from '@sravni/react-icons';
import { useDispatch } from 'react-redux';
import { FocusedFieldProps } from '../../../types';
import { namesOfOrganizationFields, WPViews } from '../../../../steps/work/constants';
import { FORM_FIELDS, LABELS } from '../../../../../../constants';
import { needed as fieldNeeded } from '../../../../steps/work/dependencies';
import { Col, ErrorTextWrapper, Row, SelectInput } from '../../../../../landing.common';
import { FocusFieldWrapper } from '../../common';
import {
    AreaOfEmploymentFieldContainer,
    EmployerInnFieldContainer,
    EmployerTypeFieldContainer,
    WorkAddressFieldContainer,
} from '../fields';
import { useWorkStepOptions } from '../../../../../../store/selectors/formStore';
import { updateWorkStepOptions } from '../../../../../../store/reducers/formStore';
import {
    EMPLOYMENT_VALUES,
    LAST_EXPERIENCE_MONTHS_SOURCE,
    LAST_EXPERIENCE_YEARS_SOURCE,
} from '../../../../../../constants/sources';
import styles from './styles.module.scss';

const { Text } = Typography;

export const WorkFieldsContainer: FC<FocusedFieldProps> = () => {
    const form = useFormContext();
    const {
        getValues,
        watch,
        formState: { dirtyFields: _, errors },
    } = form;
    const { workPlaceView } = useWorkStepOptions();
    const dispatch = useDispatch();

    const [employmentType, organizationName, ...workFieldsValues] = watch([
        FORM_FIELDS.EMPLOYMENT_TYPE,
        FORM_FIELDS.ORGANIZATION_NAME,
        ...namesOfOrganizationFields,
    ]);

    const stringifiedWorkFieldValues = workFieldsValues.join(' ');
    const stringifiedErrors = Object.keys(errors).join(' ');

    const openWorkPlaceEditor = useCallback(
        () => dispatch(updateWorkStepOptions({ workPlaceView: WPViews.wpVisible })),
        [dispatch],
    );

    const openWorkPlaceEditorManually = useCallback(
        () => dispatch(updateWorkStepOptions({ workPlaceView: WPViews.wpVisibleManual })),
        [dispatch],
    );

    const closeWorkPlaceEditor = useCallback(
        () => dispatch(updateWorkStepOptions({ workPlaceView: WPViews.wpHidden })),
        [dispatch],
    );

    const minimizeWorkPlaceEditor = useCallback(
        () => dispatch(updateWorkStepOptions({ workPlaceView: WPViews.wpShort })),
        [dispatch],
    );

    const manageWorkPlaceView = () => {
        const areAllWorkPlaceFieldsEmpty = workFieldsValues.every((val) => !val);
        const areAllWorkPlaceFieldsValid = namesOfOrganizationFields.every((fieldName) => !errors[fieldName]);
        const areAllWorkPlaceFieldsFilled = areAllWorkPlaceFieldsValid && workFieldsValues.every((val) => Boolean(val));

        if (
            [EMPLOYMENT_VALUES.RETIRED, EMPLOYMENT_VALUES.NOT_WORKING_VALUE].includes(employmentType) ||
            ((!organizationName || errors[FORM_FIELDS.ORGANIZATION_NAME]) && areAllWorkPlaceFieldsEmpty)
        ) {
            return closeWorkPlaceEditor();
        }

        if (workPlaceView === WPViews.wpVisibleManual) {
            return;
        }

        if (areAllWorkPlaceFieldsFilled) {
            return minimizeWorkPlaceEditor();
        }

        if (!areAllWorkPlaceFieldsValid) {
            return openWorkPlaceEditorManually();
        }

        openWorkPlaceEditor();
    };

    useEffect(() => {
        manageWorkPlaceView();
    }, [employmentType, organizationName, stringifiedWorkFieldValues, stringifiedErrors]);

    useEffect(() => {
        return () => {
            closeWorkPlaceEditor();
        };
    }, []);

    const isShowMinimized = useMemo(() => workPlaceView === WPViews.wpShort, [workPlaceView]);

    const isShowAllFields = useMemo(
        () => [WPViews.wpVisible, WPViews.wpVisibleManual].includes(workPlaceView),
        [workPlaceView],
    );

    if (isShowMinimized) {
        return (
            <Row gap="16" mobileGap="12">
                <Col>
                    <div className={styles['work-place__summary']}>
                        <Text size={14}>
                            {[
                                getValues(FORM_FIELDS.ORGANIZATION_NAME),
                                `ИНН ${getValues(FORM_FIELDS.EMPLOYER_INN)}`,
                                getValues(FORM_FIELDS.WORK_ADDRESS),
                            ].join(', ')}
                        </Text>
                        <button type="button" className={styles['edit-button']} onClick={openWorkPlaceEditorManually}>
                            <Text size={14} className={styles['edit-button__text']}>
                                Редактировать
                            </Text>
                            <Icon size={16} icon={<Edit />} color="blue" />
                        </button>
                    </div>
                </Col>
            </Row>
        );
    }

    if (isShowAllFields) {
        return (
            <>
                <Row gap="16" mobileGap="12">
                    {fieldNeeded(FORM_FIELDS.EMPLOYER_TYPE, employmentType) && (
                        <Col md-4>
                            <FocusFieldWrapper
                                name={FORM_FIELDS.EMPLOYER_TYPE}
                                field={<EmployerTypeFieldContainer />}
                            />
                        </Col>
                    )}
                    {fieldNeeded(FORM_FIELDS.AREA_OF_EMPLOYMENT, employmentType) && (
                        <Col md-4>
                            <FocusFieldWrapper
                                name={FORM_FIELDS.AREA_OF_EMPLOYMENT}
                                field={<AreaOfEmploymentFieldContainer />}
                            />
                        </Col>
                    )}
                    {fieldNeeded(FORM_FIELDS.EMPLOYER_INN, employmentType) && (
                        <Col md-4>
                            <FocusFieldWrapper name={FORM_FIELDS.EMPLOYER_INN} field={<EmployerInnFieldContainer />} />
                        </Col>
                    )}
                </Row>

                {fieldNeeded(FORM_FIELDS.WORK_ADDRESS, employmentType) && (
                    <Row gap="16" mobileGap="12">
                        <Col>
                            <FocusFieldWrapper name={FORM_FIELDS.WORK_ADDRESS} field={<WorkAddressFieldContainer />} />
                        </Col>
                    </Row>
                )}
            </>
        );
    }

    return null;
};

interface LastExperienceProps {
    form: UseFormReturn;
}

export const LastExperienceFields: FC<LastExperienceProps> = (props) => {
    const { form } = props;
    const {
        control,
        watch,
        getFieldState,
        trigger,
        formState: { errors, dirtyFields: _ },
    } = form;

    const [lastExperienceYear] = watch([FORM_FIELDS.LAST_EXPERIENCE_START_YEAR]);

    const { invalid: isYearInvalid } = getFieldState(FORM_FIELDS.LAST_EXPERIENCE_START_YEAR, form.formState);
    const { isDirty: isMonthDirty } = getFieldState(FORM_FIELDS.LAST_EXPERIENCE_START_MONTH, form.formState);

    const monthsOptions = useMemo(() => {
        const allMonth = LAST_EXPERIENCE_MONTHS_SOURCE.map(({ name: label, value }) => ({ label, value }));
        if (!isYearInvalid && lastExperienceYear) {
            const currentDate = new Date();
            if (currentDate.getFullYear() === Number(lastExperienceYear)) {
                return allMonth.slice(0, currentDate.getMonth() + 1);
            }
            return allMonth;
        }
        return allMonth;
    }, [isYearInvalid, lastExperienceYear]);

    const yearsOptions = useMemo(
        () => LAST_EXPERIENCE_YEARS_SOURCE.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    const validateMonthField = () => {
        trigger(FORM_FIELDS.LAST_EXPERIENCE_START_MONTH);
    };

    useEffect(() => {
        if (lastExperienceYear && !isYearInvalid && isMonthDirty) {
            validateMonthField();
        }
    }, [lastExperienceYear]);

    return (
        <Col className={styles['last-experience']}>
            <Text size={14} className={styles['last-experience__label']}>
                {LABELS[FORM_FIELDS.LAST_EXPERIENCE]}
            </Text>
            <ErrorTextWrapper
                errorText={
                    errors[FORM_FIELDS.LAST_EXPERIENCE_START_YEAR]?.message ||
                    errors[FORM_FIELDS.LAST_EXPERIENCE_START_MONTH]?.message
                }
                errorTextPosition={errors[FORM_FIELDS.LAST_EXPERIENCE_START_YEAR]?.message ? 'left' : 'right'}>
                <Row gap="16" mobileGap="12">
                    <Col sm-6>
                        <FocusFieldWrapper
                            name={FORM_FIELDS.LAST_EXPERIENCE_START_YEAR}
                            field={
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.LAST_EXPERIENCE_START_YEAR}
                                    render={({ field }) => (
                                        <SelectInput
                                            {...field}
                                            options={yearsOptions}
                                            withCrossIcon={false}
                                            label="Год"
                                            hideErrorMessage
                                            errorMessage={errors[FORM_FIELDS.LAST_EXPERIENCE_START_YEAR]?.message}
                                        />
                                    )}
                                />
                            }
                        />
                    </Col>
                    <Col sm-6>
                        <FocusFieldWrapper
                            name={FORM_FIELDS.LAST_EXPERIENCE_START_MONTH}
                            field={
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.LAST_EXPERIENCE_START_MONTH}
                                    render={({ field }) => (
                                        <SelectInput
                                            {...field}
                                            withCrossIcon={false}
                                            options={monthsOptions}
                                            disabled={isYearInvalid}
                                            label="Месяц"
                                            hideErrorMessage
                                            errorMessage={errors[FORM_FIELDS.LAST_EXPERIENCE_START_MONTH]?.message}
                                        />
                                    )}
                                />
                            }
                        />
                    </Col>
                </Row>
            </ErrorTextWrapper>
        </Col>
    );
};
